import React, { useState, useEffect } from 'react';
import {
    Card,
    Space,
    Spin,
    App,
    Button,
    Typography,
    Row,
    Col,
    Descriptions,
    Alert,
    Divider
} from 'antd';
import {
    ReloadOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { deviceCashierService, deviceRegisterService } from '../services/posApi';
import { usePageTitle } from '../context/PageTitleContext';

const { Title, Text } = Typography;

const TestDeviceInfoPage = () => {
    const { setPageTitle, setRightContent } = usePageTitle();
    const { message } = App.useApp();

    const [localStorageData, setLocalStorageData] = useState({});
    const [cashierInfo, setCashierInfo] = useState(null);
    const [registerInfo, setRegisterInfo] = useState(null);
    const [loadingCashier, setLoadingCashier] = useState(false);
    const [loadingRegister, setLoadingRegister] = useState(false);
    const [cashierError, setCashierError] = useState(null);
    const [registerError, setRegisterError] = useState(null);

    // Устанавливаем заголовок страницы
    useEffect(() => {
        setPageTitle('Тестовая страница - Информация устройства');

        setRightContent(
            <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={refreshAllData}
            >
                Обновить всё
            </Button>
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
        };
    }, [setPageTitle, setRightContent]);

    // Загрузка данных при монтировании компонента
    useEffect(() => {
        loadLocalStorageData();
        fetchCashierInfo();
        fetchRegisterInfo();
    }, []);

    // Загрузка данных из localStorage
    const loadLocalStorageData = () => {
        const data = {
            device_access_token: localStorage.getItem('device_access_token'),
            device_refresh_token: localStorage.getItem('device_refresh_token'),
            device_access_token_expires_at: localStorage.getItem('device_access_token_expires_at'),
            device_auth_code: localStorage.getItem('device_auth_code'),
            device_cashier_info: localStorage.getItem('device_cashier_info'),
            device_register_info: localStorage.getItem('device_register_info')
        };
        setLocalStorageData(data);
    };

    // Получение информации о кассире
    const fetchCashierInfo = async () => {
        setLoadingCashier(true);
        setCashierError(null);
        try {
            const info = await deviceCashierService.getCashierStatus();
            setCashierInfo(info);
            message.success('Информация о кассире загружена 1');
        } catch (error) {
            setCashierError(error.message);
            message.error(`Ошибка загрузки информации о кассире: ${error.message}`);
        } finally {
            setLoadingCashier(false);
        }
    };

    // Получение информации о кассе/устройстве
    const fetchRegisterInfo = async () => {
        setLoadingRegister(true);
        setRegisterError(null);
        try {
            const info = await deviceRegisterService.getRegisterInfo();
            setRegisterInfo(info);
            message.success('Информация о кассе загружена 1');
        } catch (error) {
            setRegisterError(error.message);
            message.error(`Ошибка загрузки информации о кассе: ${error.message}`);
        } finally {
            setLoadingRegister(false);
        }
    };

    // Обновление всех данных
    const refreshAllData = () => {
        loadLocalStorageData();
        fetchCashierInfo();
        fetchRegisterInfo();
    };

    // Форматирование времени истечения токена
    const formatTokenExpiration = (expiresAt) => {
        if (!expiresAt) return 'Не указано';
        const date = new Date(parseInt(expiresAt, 10));
        return date.toLocaleString('ru-RU');
    };

    // Проверка, истек ли токен
    const isTokenExpired = (expiresAt) => {
        if (!expiresAt) return false;
        return Date.now() > parseInt(expiresAt, 10);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Информация из localStorage */}
            <Card title={<><InfoCircleOutlined /> Данные из localStorage</>}>
                <Row gutter={[16, 16]}>
                    <Col span={12}>
                        <Card size="small" title="Токены авторизации">
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="Access Token">
                                    <Text code copyable={!!localStorageData.device_access_token}>
                                        {localStorageData.device_access_token ?
                                            `${localStorageData.device_access_token.substring(0, 20)}...` :
                                            'Отсутствует'
                                        }
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Refresh Token">
                                    <Text code copyable={!!localStorageData.device_refresh_token}>
                                        {localStorageData.device_refresh_token ?
                                            `${localStorageData.device_refresh_token.substring(0, 20)}...` :
                                            'Отсутствует'
                                        }
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Истекает">
                                    <Text type={isTokenExpired(localStorageData.device_access_token_expires_at) ? 'danger' : 'success'}>
                                        {formatTokenExpiration(localStorageData.device_access_token_expires_at)}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Код авторизации">
                                    <Text code>
                                        {localStorageData.device_auth_code || 'Отсутствует'}
                                    </Text>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card size="small" title="Сохраненная информация">
                            <Descriptions column={1} size="small">
                                <Descriptions.Item label="Информация о кассире">
                                    {localStorageData.device_cashier_info ? (
                                        <Text code copyable>
                                            {JSON.stringify(JSON.parse(localStorageData.device_cashier_info), null, 2)}
                                        </Text>
                                    ) : (
                                        <Text type="secondary">Отсутствует</Text>
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Информация о кассе">
                                    {localStorageData.device_register_info ? (
                                        <Text code copyable>
                                            {JSON.stringify(JSON.parse(localStorageData.device_register_info), null, 2)}
                                        </Text>
                                    ) : (
                                        <Text type="secondary">Отсутствует</Text>
                                    )}
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>
                </Row>
            </Card>

            <Divider />

            {/* Информация о кассире с API */}
            <Card
                title="Информация о кассире (API запрос)"
                extra={
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchCashierInfo}
                        loading={loadingCashier}
                        size="small"
                    >
                        Обновить
                    </Button>
                }
            >
                {loadingCashier ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 10 }}>
                            <Text>Загрузка информации о кассире...</Text>
                        </div>
                    </div>
                ) : cashierError ? (
                    <Alert
                        message="Ошибка загрузки"
                        description={cashierError}
                        type="error"
                        showIcon
                    />
                ) : cashierInfo ? (
                    <div>
                        <pre style={{
                            backgroundColor: '#f5f5f5',
                            padding: '16px',
                            borderRadius: '6px',
                            overflow: 'auto'
                        }}>
                            {JSON.stringify(cashierInfo, null, 2)}
                        </pre>
                    </div>
                ) : (
                    <Alert
                        message="Нет данных"
                        description="Информация о кассире не загружена"
                        type="info"
                        showIcon
                    />
                )}
            </Card>

            {/* Информация о кассе с API */}
            <Card
                title="Информация о кассе (API запрос)"
                extra={
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchRegisterInfo}
                        loading={loadingRegister}
                        size="small"
                    >
                        Обновить
                    </Button>
                }
            >
                {loadingRegister ? (
                    <div style={{ textAlign: 'center', padding: '20px 0' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 10 }}>
                            <Text>Загрузка информации о кассе...</Text>
                        </div>
                    </div>
                ) : registerError ? (
                    <Alert
                        message="Ошибка загрузки"
                        description={registerError}
                        type="error"
                        showIcon
                    />
                ) : registerInfo ? (
                    <div>
                        <pre style={{
                            backgroundColor: '#f5f5f5',
                            padding: '16px',
                            borderRadius: '6px',
                            overflow: 'auto'
                        }}>
                            {JSON.stringify(registerInfo, null, 2)}
                        </pre>
                    </div>
                ) : (
                    <Alert
                        message="Нет данных"
                        description="Информация о кассе не загружена"
                        type="info"
                        showIcon
                    />
                )}
            </Card>
        </Space>
    );
};

export default TestDeviceInfoPage;